import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Group, Stack, Text } from '@mantine/core';
import NoNds from '@/components/common/NoNds';
import { buildDealPriceModel } from '@/lib/dealPrice';
import { formatMoney } from '@/lib/format';
import type { BargainStepsInfo } from '@/types/deals';

interface DealPriceProps {
  readonly info: BargainStepsInfo;
}

// Панель цены (read-only). Продавец видит цену товара и окончательную цену продажи; покупатель/агент/банк —
// полную разбивку: Товар → Доставка(логистика){транспортировка, экспедитор, комиссия} → Итого по сделке.
// Все суммы БЕЗ НДС (маркер «без НДС» у каждой цены); НДС присутствует только в документах (счета, УПД).
// На завершённой сделке суммы фактические (план — справочно).
export default function DealPrice({ info }: DealPriceProps) {
  const { t } = useTranslation();
  const m = buildDealPriceModel(info);
  const rub = t('app.deals.rub');
  const rubPerTon = t('app.deals.rubPerTon');

  // ── Продавец: только своя выручка ────────────────────────────────────────────
  if (m.sellerView) {
    return (
      <Stack gap="sm">
        <Line label={t('app.deals.price.goodsPriceFca')}>
          <Money value={m.fcaPerTon} unit={rubPerTon} fw={600} />
        </Line>
        <Divider />
        <Line label={t('app.deals.price.goodsToReceive')}>
          <Money value={m.goodsFca.amount} unit={rub} fz="md" fw={600} />
          <PlanNote total={m.goodsFca} rub={rub} />
        </Line>
      </Stack>
    );
  }

  // ── Покупатель / агент / банк: полная разбивка ────────────────────────────────
  const showDelivery = m.logisticsTotal != null;
  return (
    <Stack gap="sm">
      <Line label={t('app.deals.price.goods')}>
        <Money value={m.goodsFca.amount} unit={rub} fz="md" fw={600} />
        <Money value={m.fcaPerTon} unit={rubPerTon} fw={400} c="gray.7" />
        <PlanNote total={m.goodsFca} rub={rub} />
      </Line>

      {showDelivery ? (
        <>
          <Divider />
          <Line label={t('app.deals.price.delivery')}>
            <Money value={m.logisticsTotal} unit={rub} fz="md" fw={600} />
          </Line>
          <Stack gap={4} pl="md">
            {m.transportationPerTon != null && m.transportationPerTon > 0 ? (
              <SubLine label={t('app.deals.price.transportation')} value={m.transportationPerTon} unit={rubPerTon} />
            ) : null}
            {m.forwarderPerTon != null && m.forwarderPerTon > 0 ? (
              <SubLine label={t('app.deals.price.forwarder')} value={m.forwarderPerTon} unit={rubPerTon} />
            ) : null}
            {m.commissionPerTon != null ? (
              <SubLine label={t('app.deals.price.commission')} value={m.commissionPerTon} unit={rubPerTon} />
            ) : null}
          </Stack>
        </>
      ) : null}

      {m.grandTotal != null ? (
        <>
          <Divider size="sm" />
          <Group justify="space-between" wrap="nowrap" align="baseline">
            <Text fz="sm" fw={600}>
              {t('app.deals.price.grandTotal')}
            </Text>
            <Money value={m.grandTotal} unit={rub} fz="lg" fw={600} />
          </Group>
        </>
      ) : null}
    </Stack>
  );
}

/** Цена + единица + маркер «без НДС». Размер/вес/цвет управляются пропами (по умолчанию sm). */
function Money({
  value,
  unit,
  fz = 'sm',
  fw,
  c,
}: {
  readonly value: number | null | undefined;
  readonly unit: string;
  readonly fz?: string;
  readonly fw?: number;
  readonly c?: string;
}) {
  return (
    <Text fz={fz} fw={fw} c={c}>
      {value == null ? (
        '—'
      ) : (
        <>
          {formatMoney(value)} {unit}
          <NoNds />
        </>
      )}
    </Text>
  );
}

/** Строка «лейбл слева — значения справа (стопкой)». */
function Line({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start" gap="md">
      <Text fz="sm" c="gray.7">
        {label}
      </Text>
      <Box ta="right">{children}</Box>
    </Group>
  );
}

/** Под-строка логистики «в т.ч. …» (ставка ₽/т, без НДС). */
function SubLine({ label, value, unit }: { readonly label: string; readonly value: number; readonly unit: string }) {
  return (
    <Group justify="space-between" wrap="nowrap" align="flex-start" gap="md">
      <Text fz="sm" c="gray.7">
        {label}
      </Text>
      <Box ta="right">
        <Money value={value} unit={unit} c="gray.7" />
      </Box>
    </Group>
  );
}

/** «план … ₽» — показываем, когда основная сумма фактическая (план как ориентир). */
function PlanNote({ total, rub }: { readonly total: { actual: boolean; planAmount: number | undefined }; readonly rub: string }) {
  const { t } = useTranslation();
  if (!total.actual || total.planAmount == null) {
    return null;
  }
  return (
    <Text fz="sm" c="gray.7">
      {t('app.deals.price.plan')} {formatMoney(total.planAmount)} {rub}
      <NoNds />
    </Text>
  );
}
